import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Form,
  Input,
  FormFeedback,
  Button,
} from "reactstrap"
import "flatpickr/dist/themes/material_blue.css"
import FlatPickr from "react-flatpickr"

import Breadcrumbs from "../../components/Common/Breadcrumb"
import { useFormik } from "formik"
import { Link, useNavigate } from "react-router-dom"
import calendar from "../../assets/images/icons/calendar.svg"
import moment from "moment"
import { useCreateIcdMutation } from "store/api/icds/icds.api"
import Spinners from "components/Common/Spinner"
import toast from "react-hot-toast"
import { interestFrequency, repaymentSchedule } from "helpers/utils"
import { formatAmount, sanitizeIntegerInput } from "helpers/common"

const defaultValues = {
  entity_name: "Prachay Capital Limited",
  amount: "",
  tenure: "",
  repayment_date: "",
  interest_rate: "",
  fund_requirement_date: "",
  interest_frequency: "",
  repayment_schedule: "SINGLE",
  repayment_terms: "",
  lockin_period: "",
  notice_period: "0",
}

const minDate = new Date()
minDate.setDate(minDate.getDate() + 7)

// const maxDate = new Date()
// maxDate.setDate(maxDate.getDate() + 365)
// minDate.setHours(0, 0, 0, 0)
// maxDate.setHours(23, 59, 59, 999)

const CreateICD = () => {
  const navigate = useNavigate()
  const [onCreate, { isLoading }] = useCreateIcdMutation()

  //meta title
  document.title = "Create ICD - Prachay"

  const onFormSubmit = async (values) => {
    try {
      const params = { ...values }
      if (values.fund_requirement_date) {
        params["fund_requirement_date"] = moment(
          values.fund_requirement_date
        ).format("YYYY-MM-DD")
      }
      const { message, success } = await onCreate(params).unwrap()
      if (success) {
        toast.success(message, { id: "toasterMessage" })
        navigate("/icd/my-requests")
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toasterMessage" })
    }
  }

  const validation = useFormik({
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      onFormSubmit(values)
    },
  })

  useEffect(() => {
    const { repayment_date, fund_requirement_date } = validation.values

    if (repayment_date && fund_requirement_date) {
      const repayment = moment(repayment_date).startOf("day")
      const fundReq = moment(fund_requirement_date).startOf("day")

      const tenureInDays = repayment.diff(fundReq, "days")

      validation.setFieldValue("tenure", `${tenureInDays}`)
      validation.setFieldValue("lockin_period", `${tenureInDays}`)
    }
  }, [
    validation.values.repayment_date,
    validation.values.fund_requirement_date,
  ])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumbs title={"Create ICD Request"} />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <Form
                    className="form-horizontal"
                    onSubmit={validation.handleSubmit}
                  >
                    <Row className="mb-2">
                      <Col xl={6}>
                        <Label for="amount" className="col-form-label">
                          Name of the Entity <span className="error">*</span>
                        </Label>
                        <Input
                          name="entity_name"
                          type="text"
                          disabled
                          className="form-control"
                          placeholder="Entity Name"
                          value={validation.values.entity_name}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.entity_name &&
                              validation.errors.entity_name
                              ? true
                              : false
                          }
                        />
                        {validation.errors.entity_name &&
                          validation.touched.entity_name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.entity_name}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label for="amount" className="col-form-label">
                          Investment Amount <span className="error">*</span>
                        </Label>
                        <Input
                          name="amount"
                          type="text"
                          className="form-control"
                          id="amount"
                          min={1}
                          placeholder="Investment Amount"
                          value={`₹ ${formatAmount(validation.values.amount)}`}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "amount"
                            )
                          }
                          invalid={
                            validation.touched.amount &&
                              validation.errors.amount
                              ? true
                              : false
                          }
                        />
                        {validation.errors.amount &&
                          validation.touched.amount ? (
                          <FormFeedback type="invalid">
                            {validation.errors.amount}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label for="amount" className="col-form-label">
                          Date of Repayment <span className="error">*</span>
                        </Label>

                        <FlatPickr
                          id="repayment_date"
                          name="repayment_date"
                          className={`form-control ${validation.touched.repayment_date &&
                            validation.errors.repayment_date &&
                            "is-invalid"
                            }`}
                          placeholder="Select date"
                          options={{
                            dateFormat: "d M, Y",
                            minDate,
                            // maxDate,
                          }}
                          value={validation.values.repayment_date}
                          onChange={(date) => {
                            const newRepaymentDate = date[0]
                            validation.setFieldValue(
                              "repayment_date",
                              newRepaymentDate
                            )
                            validation.setFieldValue(
                              "fund_requirement_date",
                              ""
                            )
                          }}
                        />
                        {validation.errors.repayment_date &&
                          validation.touched.repayment_date ? (
                          <FormFeedback type="invalid">
                            {validation.errors.repayment_date}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col xl={6}>
                        <Label
                          // htmlFor="fund_requirement_date"
                          className="col-form-label"
                        >
                          Fund Requirement Date <span className="error">*</span>
                        </Label>
                        <div className="input-wrapper">
                          <FlatPickr
                            id="fund_requirement_date"
                            name="fund_requirement_date"
                            className={`form-control ${validation.touched.fund_requirement_date &&
                              validation.errors.fund_requirement_date &&
                              "is-invalid"
                              }`}
                            placeholder="Select date"
                            options={{
                              dateFormat: "d M, Y",
                              minDate: "today",
                              maxDate: validation.values.repayment_date
                                ? moment(validation.values.repayment_date)
                                  .subtract(7, "days")
                                  .toDate()
                                : undefined,
                            }}
                            disabled={!validation.values.repayment_date}
                            selected={validation.values.fund_requirement_date}
                            value={validation.values.fund_requirement_date}
                            onChange={(date) => {
                              validation.setFieldValue(
                                "fund_requirement_date",
                                date[0]
                              )
                            }}
                          />
                          <img src={calendar} alt="" />
                        </div>

                        {validation.errors.fund_requirement_date &&
                          validation.touched.fund_requirement_date ? (
                          <FormFeedback type="invalid">
                            {validation.errors.fund_requirement_date}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label for="amount" className="col-form-label">
                          Tenure (Days) <span className="error">*</span>
                        </Label>
                        <Input
                          disabled
                          name="tenure"
                          type="text"
                          className="form-control"
                          id="tenure"
                          min={1}
                          placeholder="Tenure"
                          value={validation.values.tenure}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "tenure"
                            )
                          }
                          invalid={
                            validation.touched.tenure &&
                              validation.errors.tenure
                              ? true
                              : false
                          }
                        />
                        {validation.errors.tenure &&
                          validation.touched.tenure ? (
                          <FormFeedback type="invalid">
                            {validation.errors.tenure}
                          </FormFeedback>
                        ) : null}

                        <span
                          className="text-muted"
                          style={{
                            fontStyle: "italic",
                          }}
                        >
                          (Tenure = Date of Repayment - Fund Availability Date)
                        </span>
                      </Col>
                      <Col xl={6}>
                        <Label for="interest_rate" className="col-form-label">
                          Interest Rate (%) <span className="error">*</span>
                        </Label>
                        <Input
                          name="interest_rate"
                          type="text"
                          className="form-control"
                          id="interest_rate"
                          min={1}
                          placeholder="Interest Rate (%)"
                          value={validation.values.interest_rate}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "interest_rate"
                            )
                          }
                          invalid={
                            validation.touched.interest_rate &&
                              validation.errors.interest_rate
                              ? true
                              : false
                          }
                        />
                        {validation.errors.interest_rate &&
                          validation.touched.interest_rate ? (
                          <FormFeedback type="invalid">
                            {validation.errors.interest_rate}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col xl={6}>
                        <Label className="col-form-label">
                          Interest Payment Frequency
                          <span className="error">*</span>
                        </Label>

                        <select
                          id="interest_frequency"
                          name="interest_frequency"
                          className={`form-select pageSize ${validation.touched.interest_frequency &&
                            validation.errors.interest_frequency &&
                            "is-invalid"
                            }`}
                          value={validation.values.interest_frequency}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        >
                          <option value="">
                            {" "}
                            Select Interest Payment Frequency
                          </option>
                          {interestFrequency.map((item, index) => (
                            <option
                              key={`interestFrequency_${index}`}
                              value={item.key}
                            >
                              {item.label}
                            </option>
                          ))}
                        </select>
                        {validation.errors.interest_frequency &&
                          validation.touched.interest_frequency ? (
                          <FormFeedback type="invalid">
                            {validation.errors.interest_frequency}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      <Col xl={6}>
                        <Label className="col-form-label">
                          Repayment Schedule<span className="error">*</span>
                        </Label>

                        <select
                          id="repayment_schedule"
                          name="repayment_schedule"
                          className={`form-select pageSize ${validation.touched.repayment_schedule &&
                            validation.errors.repayment_schedule &&
                            "is-invalid"
                            }`}
                          value={validation.values.repayment_schedule}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        >
                          <option value=""> Select Repayment Schedule</option>
                          {repaymentSchedule.map((item, index) => (
                            <option
                              key={`interestFrequency_${index}`}
                              value={item.key}
                            >
                              {item.label}
                            </option>
                          ))}
                        </select>
                        {validation.errors.repayment_schedule &&
                          validation.touched.repayment_schedule ? (
                          <FormFeedback type="invalid">
                            {validation.errors.repayment_schedule}
                          </FormFeedback>
                        ) : null}
                      </Col>

                      {validation.values.repayment_schedule === "MULTIPLE" && (
                        <Col xl={6}>
                          <Label className="col-form-label">
                            No. Of Installments <span className="error">*</span>
                          </Label>
                          <Input
                            name="repayment_terms"
                            type="text"
                            className="form-control"
                            id="repayment_terms"
                            placeholder="Total Installments"
                            value={validation.values.repayment_terms}
                            onChange={(e) =>
                              sanitizeIntegerInput(
                                e,
                                validation.setFieldValue,
                                "repayment_terms"
                              )
                            }
                            invalid={
                              validation.touched.repayment_terms &&
                                validation.errors.repayment_terms
                                ? true
                                : false
                            }
                          />
                          {validation.errors.repayment_terms &&
                            validation.touched.repayment_terms ? (
                            <FormFeedback type="invalid">
                              {validation.errors.repayment_terms}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      )}
                      <Col xl={6}>
                        <Label className="col-form-label">
                          Lock-in (in days)
                          <span className="error">*</span>
                        </Label>
                        <Input
                          name="lockin_period"
                          type="text"
                          // disabled
                          className="form-control"
                          id="lockin_period"
                          placeholder="Days"
                          min={0}
                          value={validation.values.lockin_period}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "lockin_period"
                            )
                          }
                          invalid={
                            validation.touched.lockin_period &&
                              validation.errors.lockin_period
                              ? true
                              : false
                          }
                        />
                        {validation.errors.lockin_period &&
                          validation.touched.lockin_period ? (
                          <FormFeedback type="invalid">
                            {validation.errors.lockin_period}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xl={6}>
                        <Label className="col-form-label">
                          Notice Period (in days)
                          <span className="error">*</span>
                        </Label>
                        <Input
                          name="notice_period"
                          type="text"
                          className="form-control"
                          id="notice_period"
                          placeholder="Days"
                          value={validation.values.notice_period}
                          onChange={(e) =>
                            sanitizeIntegerInput(
                              e,
                              validation.setFieldValue,
                              "notice_period"
                            )
                          }
                          invalid={
                            validation.touched.notice_period &&
                              validation.errors.notice_period
                              ? true
                              : false
                          }
                        />
                        {validation.errors.notice_period &&
                          validation.touched.notice_period ? (
                          <FormFeedback type="invalid">
                            {validation.errors.notice_period}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                    <Row className="justify-content-end mt-4">
                      <Col sm={12}>
                        <div className="d-flex justify-content-end align-items-center gap-3">
                          <Link to={"/icd"}>
                            <button
                              type="button"
                              className="btn btn-cancel w-md"
                            >
                              Cancel
                            </button>
                          </Link>
                          <Button
                            type="submit"
                            className="btn btn-primary w-md"
                            disabled={isLoading}
                          >
                            {isLoading ? <Spinners /> : "Create Request"}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* <Row>
            <Col xl={12}>
              <CardTitle className="mb-4">My Fund Requests</CardTitle>
              <MyICDList />
            </Col>
          </Row> */}
        </Container>
      </div>
    </React.Fragment>
  )
}

export default CreateICD
